import {
  BubbleButton,
  BubbleFormGroup,
  BubbleInput,
  BubbleSelect,
  BubbleTable,
  BubbleTypeahead,
} from '@go-client/components/BubbleUI';
import { fme } from '@go-shared/lib/utils';
import {
  ReservableQuestionField,
  ReservableQuestionInput,
  ReservableQuestionFieldBase,
} from '@go-shared/types/reservable';
import { FormikErrors, FormikProps, FormikTouched } from 'formik';
import React, { useState } from 'react';
import { EventTemplateFormikForm } from '../Formik/EventTemplateFormik';
import { ObjectId } from 'bson';
import {
  BubbleTableFieldBehavior,
  BubbleTableFields,
  BubbleTableTemplate,
} from '../BubbleUI/BubbleTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { notUndefined } from '@go-shared/lib/functional';

interface ReservableTicketFieldProps {
  readOnly?: boolean;
  formik: FormikProps<EventTemplateFormikForm>;
  ticketIdx: number;
  onUpdate: (fields: ReservableQuestionField[]) => void;
}

interface ReservableQuestionFieldsProps {
  readOnly?: boolean;
  formik: FormikProps<EventTemplateFormikForm>;
  fields: ReservableQuestionField[];
  fieldPathPrefix: string;
  errors?: FormikErrors<ReservableQuestionFieldBase>[];
  touched?: FormikTouched<ReservableQuestionFieldBase>[];
  onUpdate: (fields: ReservableQuestionField[]) => void;
}

interface TicketFieldTableItem {
  _id: string;
  label: string;
  type: string;
  required: boolean;
  hasErrors: boolean;
  _data: ReservableQuestionField;
}

export function ReservableQuestionFields(props: ReservableQuestionFieldsProps) {
  const { readOnly, formik, fields, fieldPathPrefix, errors, touched, onUpdate } = props;

  const fieldOptions: ReservableQuestionField['type'][] = ['input', 'textarea'];
  const inputOptions: ReservableQuestionInput['settings']['type'][] = [
    'text',
    'date',
    'time',
    'number',
    'phone',
    'email',
    'checkbox',
    'radio',
  ];

  function hasSettings(type: ReservableQuestionField['type']) {
    return type === 'input' || type === 'list';
  }

  const [selectedFieldIdx, setSelectedFieldIdx] = useState(-1);
  const selectedField = fields[selectedFieldIdx];

  function makeDefaultTemplateTdProps(
    data: TicketFieldTableItem,
    fieldTdProps: React.TdHTMLAttributes<HTMLTableCellElement>
  ) {
    const expanded = selectedFieldIdx >= 0 && selectedField?._id === data._id;

    return {
      ...fieldTdProps,
      className: [
        fieldTdProps && fieldTdProps.className ? fieldTdProps.className : undefined,
        expanded ? 'bg-bubblegum-lightest' : undefined,
      ]
        .filter(notUndefined)
        .join(' '),
    };
  }

  const items: TicketFieldTableItem[] = fields.map((ff, i) => ({
    _id: ff._id,
    label: ff.label,
    type:
      ff.type === 'input'
        ? inputTypeLabelExtractor(ff.settings.type)
        : ticketFieldLabelExtractor(ff.type),
    required: ff.required,
    hasErrors: !!errors?.[i],
    _data: ff,
  }));

  const tableFields: Array<BubbleTableFields<TicketFieldTableItem>> = [
    { key: 'label' },
    { key: 'type' },
    { key: 'required' },
    { key: 'hasErrors', label: '', behavior: BubbleTableFieldBehavior.MinWidth },
    { key: 'actions', label: '', behavior: BubbleTableFieldBehavior.Condense },
  ];

  const tableTemplates: Array<BubbleTableTemplate<TicketFieldTableItem>> = [
    { key: 'label', tdProps: makeDefaultTemplateTdProps },
    { key: 'type', tdProps: makeDefaultTemplateTdProps },
    {
      key: 'required',
      tdProps: makeDefaultTemplateTdProps,
      render: item => (item.required ? 'Mandatory' : 'Optional'),
    },
    {
      key: 'hasErrors',
      tdProps: makeDefaultTemplateTdProps,
      render: item =>
        item.hasErrors ? (
          <div className="flex items-center text-crimson font-semibold">
            <FontAwesomeIcon icon="exclamation-triangle" />
            <span className="ml-1">Incomplete</span>
          </div>
        ) : (
          <span></span>
        ),
    },
    {
      key: 'actions',
      tdProps: makeDefaultTemplateTdProps,
      render: (_, idx) => (
        <div className="flex items-center">
          <BubbleButton
            variant="navy"
            className="p-1-i"
            inverse
            icon="edit"
            onClick={() => setSelectedFieldIdx(idx === selectedFieldIdx ? -1 : idx)}
          />
          <BubbleButton
            variant="slate"
            inverse
            className="p-2-i"
            icon="arrow-up"
            disabled={readOnly || idx === 0}
            onClick={() => {
              const newVal = [...fields];
              const temp = newVal[idx - 1];
              newVal[idx - 1] = newVal[idx];
              newVal[idx] = temp;
              onUpdate(newVal);
              if (selectedFieldIdx === idx) {
                setSelectedFieldIdx(idx - 1);
              }
            }}
          />
          <BubbleButton
            variant="slate"
            inverse
            className="p-2-i"
            icon="arrow-down"
            disabled={readOnly || idx === fields.length - 1}
            onClick={() => {
              const newVal = [...fields];
              const temp = newVal[idx + 1];
              newVal[idx + 1] = newVal[idx];
              newVal[idx] = temp;
              onUpdate(newVal);
              if (selectedFieldIdx === idx) {
                setSelectedFieldIdx(idx + 1);
              }
            }}
          />
          <BubbleButton
            variant="crimson"
            inverse
            className="p-2-i"
            icon="trash-alt"
            disabled={readOnly}
            onClick={() => {
              const newVal = [...fields];
              newVal.splice(idx, 1);
              onUpdate(newVal);
              setSelectedFieldIdx(-1);
            }}
          />
        </div>
      ),
    },
  ];

  function ticketFieldLabelExtractor(item: ReservableQuestionField['type']) {
    switch (item) {
      case 'input':
        return 'Short Answer';
      case 'textarea':
        return 'Long Answer';
      case 'list':
        return 'Field Group (Multiple Sub-Fields)';
      default:
        return item;
    }
  }

  function inputTypeLabelExtractor(item: ReservableQuestionInput['settings']['type']) {
    switch (item) {
      case 'text':
        return 'Text';
      case 'date':
        return 'Date';
      case 'time':
        return 'Time';
      case 'number':
        return 'Numerical';
      case 'phone':
        return 'Phone Number';
      case 'email':
        return 'Email Address';
      case 'checkbox':
        return 'Multiple Choice (Select Any)';
      case 'radio':
        return 'Multiple Choice (Select One)';
      default:
        return item;
    }
  }

  return (
    <fieldset
      disabled={readOnly}
      className="bg-white p-4 border rounded shadow grid grid-cols-1 gap-4 justify-items-start"
    >
      <div className="text-xs text-slate italic">
        Collect additional information from guests to understand who is attending.
      </div>
      {items.length > 0 && (
        <BubbleTable
          responsive
          striped
          slim
          hideHeader
          bordered
          items={items}
          fields={tableFields}
          templates={tableTemplates}
          keyExtractor={item => item._id}
          detailsKey={selectedField?._id || ''}
          onRowClick={(_, idx) => setSelectedFieldIdx(idx === selectedFieldIdx ? -1 : idx)}
          detailsClassName="p-4 bg-bubblegum-lightest"
          detailsRender={() => (
            <div className="w-full grid grid-cols-2 gap-4 bg-eggshell shadow p-3 border">
              <BubbleFormGroup
                label="Label"
                className="flex-grow"
                error={fme(errors?.[selectedFieldIdx], touched?.[selectedFieldIdx], 'label')}
                asterisk
              >
                <BubbleInput
                  type="text"
                  block
                  value={selectedField.label}
                  formik={[formik, `${fieldPathPrefix}[${selectedFieldIdx}].label`]}
                  readOnly={readOnly}
                />
              </BubbleFormGroup>
              <div className="flex-grow flex items-center">
                <BubbleFormGroup label="Format" className="flex-grow mr-3">
                  <BubbleSelect
                    block
                    allowUndefined={false}
                    options={fieldOptions}
                    value={selectedField.type}
                    readOnly={readOnly}
                    name={`${fieldPathPrefix}[${selectedFieldIdx}].type`}
                    labelExtractor={ticketFieldLabelExtractor}
                    onValue={val => {
                      const newVal = [...fields];
                      newVal.splice(selectedFieldIdx, 1, {
                        ...newVal[selectedFieldIdx],
                        type: val as any,
                      });
                      onUpdate(newVal);
                    }}
                    onBlur={formik.handleBlur}
                  />
                </BubbleFormGroup>
                <BubbleFormGroup label="Req">
                  <BubbleInput
                    type="checkbox"
                    checked={selectedField.required}
                    readOnly={readOnly}
                    formik={[formik, `${fieldPathPrefix}[${selectedFieldIdx}].required`]}
                  />
                </BubbleFormGroup>
              </div>
              {hasSettings(selectedField.type) && (
                <div className="col-span-2 grid grid-cols-1 gap-4">
                  {selectedField.type === 'input' && (
                    <>
                      <BubbleFormGroup label="Type">
                        <BubbleSelect
                          block
                          allowUndefined={false}
                          options={inputOptions}
                          readOnly={readOnly}
                          value={selectedField.settings.type}
                          labelExtractor={inputTypeLabelExtractor}
                          name={`${fieldPathPrefix}[${selectedFieldIdx}].settings.type`}
                          onValue={val => {
                            const newVal = [...fields];
                            newVal.splice(selectedFieldIdx, 1, {
                              ...newVal[selectedFieldIdx],
                              settings: {
                                ...(newVal[selectedFieldIdx] as ReservableQuestionInput).settings,
                                type: val,
                              } as any,
                            });
                            onUpdate(newVal);
                          }}
                          onBlur={formik.handleBlur}
                        />
                      </BubbleFormGroup>
                      {(selectedField.settings.type === 'checkbox' ||
                        selectedField.settings.type === 'radio') && (
                        <BubbleFormGroup label="Choices">
                          <BubbleTypeahead
                            block
                            options={selectedField.settings.options.map(o => ({
                              key: o._id,
                              item: o,
                            }))}
                            readOnly={readOnly}
                            placeholder="Enter choice title..."
                            menuClassName="max-h-80 overflow-y-auto"
                            allowNew
                            noSuggestionsClick
                            onReorder={opts => {
                              const newVal = [...fields];
                              (
                                newVal[selectedFieldIdx] as ReservableQuestionInput
                              ).settings.options = opts.map(i => i.item);
                              onUpdate(newVal);
                            }}
                            alwaysShowSuggestions={selectedField.settings.options.length > 0}
                            suggestionsText="No similar item found"
                            suggestionClassName="p-1-i"
                            suggestionsRenderFn={(_, opt) => (
                              <div className="flex items-center">
                                <BubbleInput
                                  type="text"
                                  block
                                  appearance="bare"
                                  value={opt.item.name}
                                  className="flex-grow"
                                  inputClassName="px-3 py-1 rounded"
                                  onValue={val => {
                                    const newVal = [...fields];
                                    const newOpts = (
                                      newVal[selectedFieldIdx] as ReservableQuestionInput
                                    ).settings.options;
                                    const optsIdx = newOpts.findIndex(o => o._id === opt.item._id);
                                    if (optsIdx < 0) return;

                                    newOpts[optsIdx].name = val;
                                    (
                                      newVal[selectedFieldIdx] as ReservableQuestionInput
                                    ).settings.options = newOpts;
                                    onUpdate(newVal);
                                  }}
                                  onBlur={formik.handleBlur}
                                />
                              </div>
                            )}
                            onCreate={val => {
                              const newVal = [...fields];
                              const newOpts = (newVal[selectedFieldIdx] as ReservableQuestionInput)
                                .settings.options;
                              newOpts.push({ _id: new ObjectId().toHexString(), name: val });
                              onUpdate(newVal);
                            }}
                            onTrash={val => {
                              const newVal = [...fields];
                              const newOpts = (newVal[selectedFieldIdx] as ReservableQuestionInput)
                                .settings.options;
                              (
                                newVal[selectedFieldIdx] as ReservableQuestionInput
                              ).settings.options = newOpts.filter(o => o._id !== val.item._id);
                              onUpdate(newVal);
                              setSelectedFieldIdx(-1);
                            }}
                          />
                        </BubbleFormGroup>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        />
      )}
      {readOnly && fields.length < 1 && <span className="text-smoke italic">None</span>}
      {!readOnly && (
        <BubbleButton
          inverse
          className="p-0-i"
          onClick={() => {
            onUpdate([
              ...fields,
              {
                _id: new ObjectId().toHexString(),
                label: '',
                type: 'input',
                required: true,
                settings: {
                  type: 'text',
                  options: [],
                  constraints: { min: 0, max: 100, step: 1 },
                },
              },
            ]);
            setSelectedFieldIdx(fields.length);
          }}
          variant="navy"
          icon="plus"
        >
          Add Question
        </BubbleButton>
      )}
    </fieldset>
  );
}

export function ReservableTicketFields(props: ReservableTicketFieldProps) {
  const { readOnly, formik, ticketIdx, onUpdate } = props;

  const fields = formik.values.reservableTickets[ticketIdx]?.fields || [];
  const reservableTicketError = formik.errors.reservableTickets?.[ticketIdx];
  const reservableTicketTouched = formik.touched.reservableTickets?.[ticketIdx];

  const errors =
    reservableTicketError && typeof reservableTicketError !== 'string'
      ? (reservableTicketError.fields as FormikErrors<ReservableQuestionFieldBase>[] | undefined)
      : undefined;

  const touched = reservableTicketTouched?.fields as
    | FormikTouched<ReservableQuestionFieldBase>[]
    | undefined;

  return (
    <ReservableQuestionFields
      readOnly={readOnly}
      formik={formik}
      fields={fields}
      fieldPathPrefix={`reservableTickets[${ticketIdx}].fields`}
      errors={errors}
      touched={touched}
      onUpdate={onUpdate}
    />
  );
}
